/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import get from 'lodash/get';
import { getFirstDivision } from './permitUtils';
import * as types from '../constants';

export const ClinNum = 0;
export const TransactionNum = 1;
export const UseNum = 2;
export const TicketNum = 3;
export const AttributeNum = 4;
export const PosNum = 5;
export const TaxNum = 6;
export const ReservationNum = 1000;

export const feeTypes = new Map();
feeTypes.set(types.CLIN_FEE, ClinNum);
feeTypes.set(types.TRANSACTION_FEE, TransactionNum);
feeTypes.set(types.USE_FEE, UseNum);
feeTypes.set(types.TICKET_FEE, TicketNum);
feeTypes.set(types.ATTRIBUTE_FEE, AttributeNum);
feeTypes.set(types.POS_FEE, PosNum);
feeTypes.set(types.TAX_FEE, TaxNum);
feeTypes.set(types.RESERVATION_FEE, ReservationNum);

export const feeNames = new Map();
feeNames.set(ClinNum, 'Reservation Fee');
feeNames.set(TransactionNum, 'Transaction Fee');
feeNames.set(UseNum, 'Use Fee');
feeNames.set(TicketNum, 'Ticket Fee');
feeNames.set(AttributeNum, 'Attribute Fee');
feeNames.set(PosNum, 'POS Fee');
feeNames.set(TaxNum, 'Tax');
feeNames.set(ReservationNum, 'Reservation Fee');

export const convertCentsToCartValue = (centAmount) => {
    return centAmount * 1000; // cent to thousandths of a cent
};

export const convertCartValueToCents = (cartAmount) => {
    return cartAmount / 1000; // thousandths of a cent to cent
};

export const sumLineItems = (lineItems) => {
    let total = 0;
    for (let i = 0; i < lineItems.length; i++) {
        total += get(lineItems[i], 'UnitPrice', 0) * get(lineItems[i], 'Quantity', 0);
    }
    return convertCartValueToCents(total);
};

// generates line items
// errors are also generated, but for now they are not used
export const generateLineItems = (rawIssuance, rawDivision, rawPermit) => {
    let issuance = null;
    let division = null;
    let permit = null;

    // In some cases the division is not set in the state - we _must_ pass a division in
    let divisionInIssuance;
    if (Object.keys(rawDivision).length === 0) {
        if (rawIssuance.division_id) {
            divisionInIssuance = rawPermit.divisions[rawIssuance.division_id];
        }
        if (!divisionInIssuance) {
            divisionInIssuance = getFirstDivision(rawPermit.divisions);
        }
    } else {
        divisionInIssuance = rawDivision;
    }

    // ensure that all parameters can be parsed as json
    try {
        issuance = JSON.parse(JSON.stringify(rawIssuance));
        division = JSON.parse(JSON.stringify(divisionInIssuance));
        permit = JSON.parse(JSON.stringify(rawPermit));
    } catch (e) {
        return [];
    }

    if (window.GenerateLineItems) {
        const [lineItems] = window.GenerateLineItems(
            JSON.stringify(issuance),
            JSON.stringify(division),
            JSON.stringify(permit)
        );
        return lineItems;
    }

    return [];
};

export const convertLineItemsToFeesMap = (fees) => {
    const feesMap = new Map();
    feesMap.set(types.RESERVATION_FEE, 0);
    feesMap.set(types.USE_FEE, 0);
    feesMap.set(types.ATTRIBUTE_FEE, 0);
    feesMap.set(types.ATTRIBUTE_FEE_DESCRIPTION, '');

    fees.forEach((fee) => {
        if (
            fee.FeeType === feeTypes.get(types.CLIN_FEE) ||
            fee.FeeType === feeTypes.get(types.RESERVATION_FEE)
        ) {
            feesMap.set(
                types.RESERVATION_FEE,
                feesMap.get(types.RESERVATION_FEE) + fee.Quantity * fee.UnitPrice
            );
        }

        if (fee.FeeType === feeTypes.get(types.USE_FEE)) {
            feesMap.set(
                types.USE_FEE,
                feesMap.get(types.USE_FEE) + fee.Quantity * fee.UnitPrice
            );
        }

        if (fee.FeeType === feeTypes.get(types.ATTRIBUTE_FEE)) {
            feesMap.set(
                types.ATTRIBUTE_FEE,
                feesMap.get(types.ATTRIBUTE_FEE) + fee.Quantity * fee.UnitPrice
            );
            const description = fee.Description ? fee.Description : 'Other Fee';
            feesMap.set(types.ATTRIBUTE_FEE_DESCRIPTION, description);
        }
    });

    feesMap.set(
        types.RESERVATION_FEE,
        convertCartValueToCents(feesMap.get(types.RESERVATION_FEE))
    );
    feesMap.set(types.USE_FEE, convertCartValueToCents(feesMap.get(types.USE_FEE)));
    feesMap.set(
        types.ATTRIBUTE_FEE,
        convertCartValueToCents(feesMap.get(types.ATTRIBUTE_FEE))
    );
    return feesMap;
};
